import axios from 'axios';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { trackButtonClick } from '../../analytics/mixpanel';
import { DeleteDocIcon, EditDocIcon } from '../../assets';
import DownloadOutlined from '../../assets/docchat/DownloadOutlined';
import { ROOT_URL } from '../../config';
import {
  deleteComparison,
  getSavedComparisons,
  handleExport,
} from '../../Redux/FileCompare/fileCompareActions';
import { getValidAccessToken } from '../../utils/authUtils';
import { useSelectedComparisonsData } from '../../utils/hooks/useSelectedComparisonsData';
import InlineStyled from '../InlineStyled/InlineStyled';
import Button from '../UI/Button';
import ConfirmationModal from '../UI/ConfirmationModal';
import UpdateTextModal from '../UpdateTextModal';

const initialModalData = {
  edit: false,
  delete: false,
  loader: null,
};

const HeaderDocCompare = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedData } = useSelectedComparisonsData();
  const [modalData, setModalData] = useState(initialModalData);
  const selectedComparisonName = selectedData?.title;

  const handleModal = (name) => {
    setModalData((prev) => ({ ...prev, [name]: true }));
  };

  const closeModal = () => {
    setModalData({ ...initialModalData });
  };

  const handleDelete = async () => {
    setModalData((prev) => ({ ...prev, loader: 'DELETE' }));
    trackButtonClick({
      type: 'Delete previously compared document',
    });
    try {
      await dispatch(deleteComparison(selectedData.id));
      closeModal();
      navigate('/doccompare');
      await dispatch(getSavedComparisons());
    } finally {
      if (modalData.loader) {
        closeModal();
      }
    }
  };

  const handleUpdate = async (value) => {
    setModalData((prev) => ({ ...prev, loader: 'UPDATE' }));
    trackButtonClick({
      type: 'Delete previously compared document',
    });
    try {
      await axios.patch(
        `${ROOT_URL}/context_comparison/${selectedData?.id}/`,
        {
          operation: 'rename_title',
          comparison_title: value,
        },
        {
          headers: {
            Authorization: `Bearer ${await getValidAccessToken()}`,
          },
        }
      );
      closeModal();
      await dispatch(getSavedComparisons());
    } finally {
      if (modalData.loader) {
        closeModal();
      }
    }
  };

  const renderHeader = () => {
    return <div className='mb-16 fs-18 fw-500'>Rename comparison</div>;
  };

  return (
    <>
      <InlineStyled
        className='flex fs-14 align-center'
        style={{
          gap: '8px',
          width: 'calc(100vw - 476px)',
          justifyContent: 'space-between',
        }}
      >
        <InlineStyled className='flex align-center' style={{ gap: '8px' }}>
          {selectedComparisonName && selectedComparisonName !== 'undefined' && (
            <span style={{ color: '--grey-12', wordBreak: 'break-all' }}>
              {selectedComparisonName}
            </span>
          )}
          <button onClick={() => handleModal('edit')} title='Rename this comparison'>
            <EditDocIcon size={18} />
          </button>
        </InlineStyled>
        <InlineStyled className='flex align-center' style={{ gap: '20px' }}>
          <Button
            icon={<DownloadOutlined fill='var(--primary-color)' />}
            variant='outlined'
            color='primary'
            className='btn-m'
            onClick={() => dispatch(handleExport(selectedData?.id))}
          >
            Export
          </Button>
          <button title='Delete this comparison' onClick={() => handleModal('delete')}>
            <DeleteDocIcon size={18} fill='var(--danger-6)' />
          </button>
        </InlineStyled>
      </InlineStyled>
      <UpdateTextModal
        isOpen={modalData.edit}
        initialText={selectedData?.title}
        onClose={closeModal}
        onSave={handleUpdate}
        loading={modalData.loader === 'UPDATE'}
        renderHeader={renderHeader}
      />
      <ConfirmationModal
        isOpen={modalData.delete}
        text={`Are you sure you want to delete this "${selectedData?.title}" comparison??`}
        onCancel={closeModal}
        onSubmit={handleDelete}
        loading={modalData.loader === 'DELETE'}
        submitButtonProps={{ danger: true }}
      />
    </>
  );
};

export default HeaderDocCompare;
